import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bone, Stethoscope, Flame, Truck, Heart, Sparkles, PackageCheck, ArrowRight } from "lucide-react";
import { api, apiError, scrollToId, money } from "@/lib/helpers";
import { IMPACT_TIERS, THANK_YOU_NAMES, SANCTUARY_STATS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const ICONS = { bone: Bone, stethoscope: Stethoscope, flame: Flame, truck: Truck };

export default function Bento({ campaign }) {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [monthly, setMonthly] = useState(true);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onPreset = (e) => {
      setAmount(e.detail);
      setCustom("");
      setTimeout(() => scrollToId("donate"), 60);
      toast(`Great choice — $${e.detail} selected`, { description: "Add your name below and make it official." });
    };
    window.addEventListener("wnt:donate-preset", onPreset);
    return () => window.removeEventListener("wnt:donate-preset", onPreset);
  }, []);

  const pct = campaign ? Math.min(100, campaign.percent || 0) : 0;
  const finalAmount = custom ? parseInt(custom, 10) || 0 : amount;

  const donate = async () => {
    if (!donorName.trim() || !/^\S+@\S+\.\S+$/.test(donorEmail)) {
      toast.error("Add your name and a valid email so we can send your receipt.");
      return;
    }
    if (finalAmount < 1) { toast.error("Choose an amount first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "donation", amount: finalAmount, monthly,
        name: donorName, email: donorEmail, origin_url: window.location.origin,
      });
      toast.success("Taking you to secure Stripe checkout…");
      setTimeout(() => { window.location.href = data.checkout_url; }, 700);
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <section id="donate" className="relative bg-sand py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="eyebrow">Ways to give</p>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal tracking-tight">
            Every dollar climbs the mountain with them.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Goal Meter — big card */}
          <div data-testid="goal-meter-card" className="md:col-span-12 lg:col-span-7 lg:row-span-2 rounded-3xl text-sand p-6 sm:p-10 shadow-2xl shadow-pine/20 relative overflow-hidden grain isolate">
            <img src="/sections/winter_puppy.webp" alt="Golden retriever puppy in the winter snow" className="absolute inset-0 h-full w-full object-cover -z-10" />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-charcoal/70 to-charcoal/90 -z-10" />
            <p className="dark-eyebrow">Winter 2026 Emergency Fund</p>
            <h3 className="mt-2 font-serif text-2xl sm:text-3xl" data-testid="goal-meter-title">
              {campaign?.title || "Winter Food & Medical Emergency Fund"}
            </h3>
            <div className="mt-8 flex items-end gap-3">
              <span className="font-serif text-4xl sm:text-5xl text-gold-soft" data-testid="goal-meter-raised">{money(campaign?.raised || 0)}</span>
              <span className="pb-1.5 font-mono text-sm text-sand/60">raised of {money(campaign?.goal || 0)}</span>
            </div>
            <div className="mt-4 h-4 w-full rounded-full bg-pine-deep/80 overflow-hidden">
              <motion.div
                data-testid="goal-meter-progress"
                className="h-full rounded-full bg-gradient-to-r from-gold to-gold-soft"
                initial={{ width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <p className="mt-3 font-mono text-xs text-sand/60" data-testid="goal-meter-percent">
              {pct}% funded · {campaign?.donor_count || 0} neighbors have given
            </p>

            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold-soft mb-3">Pick your gift</p>
              <div className="flex flex-wrap gap-2.5">
                {[25, 50, 100, 250].map((a) => (
                  <button key={a} data-testid={`goal-preset-${a}`} onClick={() => { setAmount(a); setCustom(""); }}
                    className={`rounded-full px-5 py-2.5 font-mono text-sm font-semibold transition-all ${
                      !custom && amount === a ? "bg-gold text-charcoal scale-105" : "bg-pine-deep/70 text-sand hover:bg-pine-mid"
                    }`}>
                    ${a}
                  </button>
                ))}
                <input
                  data-testid="goal-custom-amount-input"
                  type="number" min="1" placeholder="Custom $" value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="w-28 rounded-full bg-pine-deep/70 px-4 py-2.5 font-mono text-sm text-sand placeholder:text-sand/40 outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <label data-testid="goal-monthly-toggle" className="mt-4 inline-flex cursor-pointer items-center gap-3 text-sm text-sand/85">
                <button
                  role="switch" aria-checked={monthly} data-testid="goal-monthly-switch"
                  onClick={() => setMonthly(!monthly)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${monthly ? "bg-gold" : "bg-pine-deep"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-sand transition-all ${monthly ? "left-[22px]" : "left-0.5"}`} />
                </button>
                Make it monthly — a Sanctuary Sponsor keeps the kennels stocked all winter
              </label>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input data-testid="donor-name-input" placeholder="Your name" value={donorName} onChange={(e) => setDonorName(e.target.value)}
                  className="rounded-xl bg-pine-deep/70 px-4 py-3 text-sm text-sand placeholder:text-sand/40 outline-none focus:ring-2 focus:ring-gold" />
                <input data-testid="donor-email-input" type="email" placeholder="Email for your receipt" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)}
                  className="rounded-xl bg-pine-deep/70 px-4 py-3 text-sm text-sand placeholder:text-sand/40 outline-none focus:ring-2 focus:ring-gold" />
              </div>
              <button data-testid="goal-meter-donate-btn" onClick={donate} disabled={busy}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-bold text-charcoal transition-all hover:bg-gold-soft hover:scale-[1.01] active:scale-95 disabled:opacity-60">
                <Heart className="h-5 w-5" />
                {busy ? "Recording your pledge…" : `Give ${money(finalAmount)}${monthly ? " / month" : ""}`}
              </button>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-sand/40">
                Secure checkout powered by Stripe · every dollar goes to the animals
              </p>
            </div>
          </div>

          {/* Recent supporters ticker */}
          <div data-testid="recent-supporters-card" className="md:col-span-6 lg:col-span-5 rounded-3xl bg-tinted p-6 sm:p-8 border border-stone-200">
            <p className="eyebrow">On the gratitude wall</p>
            <h3 className="mt-2 font-serif text-xl text-charcoal">Thank you to these friends</h3>
            <ul className="mt-5 space-y-3">
              {THANK_YOU_NAMES.map((name, i) => (
                <li key={name} data-testid={`gratitude-name-${i + 1}`} className="flex items-center gap-3 border-b border-stone-200/80 pb-3 last:border-0">
                  <Heart className="h-4 w-4 shrink-0 text-gold" />
                  <span className="text-sm text-bark">{name}</span>
                </li>
              ))}
            </ul>
            <div data-testid="special-thanks-tara" className="mt-4 flex items-center gap-3 rounded-2xl bg-gold/15 border border-gold/40 p-3.5">
              <Heart className="h-4 w-4 shrink-0 text-gold-deep" />
              <p className="text-sm text-charcoal"><span className="font-bold">Special thanks to Tara</span> — one of the rescue's wonderful volunteers.</p>
            </div>
          </div>

          {/* Sponsor spotlight */}
          <div data-testid="sponsor-spotlight-card" className="md:col-span-6 lg:col-span-5 rounded-3xl overflow-hidden relative min-h-[300px] flex items-end">
            <img src="/lucy.webp"
              alt="Lucy the chihuahua mix snug under her favorite blanket" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
            <div className="relative p-6 sm:p-8">
              <p className="dark-eyebrow">Sponsor spotlight</p>
              <h3 className="mt-2 font-serif text-2xl text-sand">Meet Lucy</h3>
              <p className="mt-2 max-w-sm text-sm text-sand/80">
                World-champion blanket burrower and professional snuggler. $30/mo keeps Lucy in cozy bedding, good food and whatever care she needs.
              </p>
              <button data-testid="sponsor-spotlight-cta" onClick={() => window.dispatchEvent(new CustomEvent("wnt:open-animal", { detail: "Lucy" }))}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-charcoal hover:bg-gold-soft transition-all hover:scale-[1.03]">
                Sponsor Lucy for $30/mo <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Impact tiers */}
          <div data-testid="impact-tiers-card" className="md:col-span-6 lg:col-span-4 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 shadow-sm">
            <p className="eyebrow">Your $ buys</p>
            <h3 className="mt-2 font-serif text-xl text-charcoal">See exactly what a gift does</h3>
            <div className="mt-5 space-y-3">
              {IMPACT_TIERS.map((t) => {
                const Icon = ICONS[t.icon];
                return (
                  <button key={t.amount} data-testid={`impact-tier-${t.amount}`}
                    onClick={() => window.dispatchEvent(new CustomEvent("wnt:donate-preset", { detail: t.amount }))}
                    className="group flex w-full items-center gap-4 rounded-2xl bg-tinted/70 p-3.5 text-left transition-all hover:bg-tinted hover:translate-x-1">
                    {t.img ? (
                      <img src={t.img} alt="Milk-Bone Maro Snacks treats" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-pine text-gold-soft">
                        <Icon className="h-5 w-5" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block font-mono text-sm font-bold text-pine">${t.amount} · {t.title}</span>
                      <span className="block text-xs text-bark leading-snug">{t.desc}</span>
                    </span>
                    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-stone-400 transition-transform group-hover:translate-x-1" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Wishlist teaser */}
          <div data-testid="wishlist-teaser-card" className="md:col-span-6 lg:col-span-4 rounded-3xl overflow-hidden relative min-h-[320px] flex items-end">
            <img src="/sections/wishlist_corner.webp"
              alt="A cozy pet corner with a plush dog bed, cat tree and treat jar" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/40 to-charcoal/10" />
            <div className="relative p-6 sm:p-8">
              <p className="dark-eyebrow">Supplies wishlist</p>
              <h3 className="mt-2 font-serif text-xl text-sand">Rather give goods? The pups and kitties have a list.</h3>
              <p className="mt-3 text-sm text-sand/80">
                Cozy beds, cat trees, food and vet care — practical things our dogs and cats need this week.
              </p>
              <button data-testid="wishlist-teaser-cta" onClick={() => scrollToId("wishlist")}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-charcoal hover:bg-gold-soft transition-all hover:scale-[1.03]">
                Browse the wishlist <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Stat card */}
          <div data-testid="sanctuary-stat-card" className="md:col-span-6 lg:col-span-4 rounded-3xl overflow-hidden relative min-h-[300px] flex items-center">
            <img src="/sections/sanctuary_today.webp"
              alt="Autumn hills and lake surrounding the sanctuary" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/50 to-charcoal/35" />
            <div className="relative w-full p-6 sm:p-8">
              <p className="dark-eyebrow">The sanctuary today</p>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6">
                {SANCTUARY_STATS.map((s) => (
                  <div key={s.label}>
                    <p className="font-serif text-2xl sm:text-3xl text-sand">{s.value}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-sand/70 leading-snug">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
